import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import scheduleAssistService, {
  ACCESS_CAUSE,
  IScheduleAssistAccess,
} from 'services/scheduleAssist';
import { RootState } from 'store/index';

export type { IScheduleAssistAccess } from 'services/scheduleAssist';

interface IInitialState {
  /** Loaded once per session. `null` until the access check resolves. */
  access: IScheduleAssistAccess | null;
  /** True while the access check is in flight. */
  loading: boolean;
}

const initialState: IInitialState = {
  access: null,
  loading: false,
};

/**
 * Calls `GET /SchedulingPod/access` to determine if the current user is
 * approved to use the Schedule Assist tab. The Sidebar dispatches this on
 * mount; the tab is hidden only when the tenant module is off (nothing the
 * user can act on) — user-level denials render a guide state in the panel.
 *
 * This slice is about TAB visibility only. Book Now visibility inside the panel is
 * gated separately, from the panel's own fetch (getPodSetupState in
 * services/scheduleAssist) via bookNowVisible in ScheduleAssist.tsx — nothing here reads
 * bookNowEnabled, and it is deliberately not threaded through this slice's state so a
 * later reader does not assume this is where that gating lives.
 */
export const ThunkGetScheduleAssistAccess = createAsyncThunk<IScheduleAssistAccess>(
  'scheduleAssistAccess/get',
  async () => {
    try {
      const { data } = await scheduleAssistService.getMyAccess();
      return {
        hasAccess: Boolean(data?.hasAccess),
        reason: data?.reason ?? null,
        cause: data?.cause ?? null,
      };
    } catch {
      // Treat any failure as no-access so the tab stays hidden until the
      // backend recovers; a user who lost access transiently won't see a
      // half-broken UI.
      return { hasAccess: false, reason: null, cause: null };
    }
  },
);

const scheduleAssistAccess = createSlice({
  name: 'scheduleAssistAccess',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(ThunkGetScheduleAssistAccess.pending, state => {
        state.loading = true;
      })
      .addCase(ThunkGetScheduleAssistAccess.fulfilled, (state, action) => {
        state.loading = false;
        state.access = action.payload;
      })
      .addCase(ThunkGetScheduleAssistAccess.rejected, state => {
        state.loading = false;
        state.access = { hasAccess: false, reason: null, cause: null };
      });
  },
});

export default scheduleAssistAccess.reducer;

/** True only when the access check has completed AND returned `hasAccess: true`. */
export const hasScheduleAssistAccessSelector = (state: RootState): boolean =>
  state.scheduleAssistAccess.access?.hasAccess === true;

/**
 * Tab visibility: shown for approved users AND for user-level denials the panel
 * can explain (not Ready / account disabled). Hidden when the module is off or
 * the check failed outright.
 */
export const scheduleAssistTabVisibleSelector = (state: RootState): boolean => {
  const access = state.scheduleAssistAccess.access;
  if (!access) return false;
  if (access.hasAccess) return true;
  return access.cause === ACCESS_CAUSE.notReady || access.cause === ACCESS_CAUSE.accountDisabled;
};

/** Whole access object (or null while still loading). Useful for showing the reason in admin/debug views. */
export const scheduleAssistAccessSelector = (state: RootState): IScheduleAssistAccess | null =>
  state.scheduleAssistAccess.access;

/** True while the access check is still in flight — prevents tab flicker on first paint. */
export const scheduleAssistAccessLoadingSelector = (state: RootState): boolean =>
  state.scheduleAssistAccess.loading;
